import React, { useState, useMemo } from 'react';
import './TropicalSpice.css';
import { paginateMenu } from '../../../utils/menuPagination';
import { exportPtPagesToPdf } from '../../../utils/exportPtPagesToPdf';
import ExportPdfFab from '../../../components/ExportPdfFab';

const TropicalSpice = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const rawMenus = data?.menus || [];

    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Determine which pages to display
    const visiblePages = useMemo(() => {
        if (isSingleMenu) {
            return rawMenus.flatMap(menu => paginateMenu(menu));
        }
        const activeMenu = rawMenus[activeMenuIndex];
        return activeMenu ? paginateMenu(activeMenu) : [];
    }, [isSingleMenu, rawMenus, activeMenuIndex]);

    const handleNext = () => {
        if (activeMenuIndex < rawMenus.length - 1) {
            setActiveMenuIndex(p => p + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (activeMenuIndex > 0) {
            setActiveMenuIndex(p => p - 1);
            window.scrollTo(0, 0);
        }
    };

    if (!data) return <div className="loading">Loading...</div>;

    return (
        <div className="ts-root">
            {/* Navigation - Only show in Multi-Menu Mode (Preview) */}
            {!isSingleMenu && (
                <div className="tmpl-nav-container no-print" style={{ justifyContent: 'center' }}>
                    <div className="tmpl-nav-controls">
                        <button className="tmpl-nav-btn" onClick={handlePrev} disabled={activeMenuIndex === 0}>
                            ← Prev Menu
                        </button>
                        <div className="tmpl-page-indicator">
                            {rawMenus[activeMenuIndex]?.name || `Menu ${activeMenuIndex + 1}`}
                            <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                                ({activeMenuIndex + 1}/{rawMenus.length})
                            </span>
                        </div>
                        <button className="tmpl-nav-btn" onClick={handleNext} disabled={activeMenuIndex === rawMenus.length - 1}>
                            Next Menu →
                        </button>
                    </div>
                </div>
            )}

            <ExportPdfFab onClick={() => exportPtPagesToPdf({ filename: `${restaurant.name || 'menu'}-tropical-spice.pdf` })} />

            <div className="ts-menu-container">
                {visiblePages.map((page, index) => {
                    const categories = (page.categories || []);

                    // Header logic:
                    // PDF: Only on very first page
                    // Preview: On first page of the active menu
                    const showHeader = isSingleMenu ? (index === 0) : (page.chunkIndex === 0);

                    // Menu Title Logic: Only show on first page of the menu chunk
                    const showMenuTitle = page.chunkIndex === 0;

                    return (
                        <article key={`${page.id || page.name}-${index}`} className="pt-page active">
                            <div className="ts-texture-overlay"></div>

                            {showHeader && (
                                <header className="ts-header">
                                    <div className="ts-logo-box">
                                        <div className="ts-leaf-icon">🍃</div>
                                        <h1 className="ts-name">{restaurant.name}</h1>
                                        <div className="ts-leaf-icon">🍂</div>
                                    </div>
                                    <div className="ts-divider"></div>
                                    {restaurant.tagline && <p className="ts-tagline">{restaurant.tagline}</p>}
                                </header>
                            )}

                            <main className="ts-menu">
                                {showMenuTitle && (
                                    <h2 className="ts-menu-name">{page.name}</h2>
                                )}
                                <div className="ts-categories">
                                    {categories.map(category => (
                                        <section key={category.id} className="ts-category">
                                            <div className="ts-cat-label">
                                                <span>{category.name}</span>
                                            </div>
                                            <div className="ts-items">
                                                {(category.items || []).map(item => (
                                                    <div key={item.id} className="ts-item">
                                                        <div className="ts-item-main">
                                                            <div className="ts-item-title">
                                                                {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                                    <span className={`ts-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                        <span className="ts-veg-mark"></span>
                                                                    </span>
                                                                )}
                                                                <span className="ts-item-name">{item.name}</span>
                                                            </div>
                                                            <span className="ts-item-price">₹{item.price}</span>
                                                        </div>
                                                        {item.description && <p className="ts-item-desc">{item.description}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </section>
                                    ))}
                                </div>
                            </main>

                            <footer className="ts-footer">
                                <div className="ts-footer-content">
                                    {restaurant.address && <p>{restaurant.address}</p>}
                                    {restaurant.phoneNumber && <p>Delivery: {restaurant.phoneNumber}</p>}
                                </div>
                                <div className="ts-footer-leaves">🌿🌿🌿</div>
                            </footer>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default TropicalSpice;
